import React, { useEffect, useMemo, useState } from "react";
import { http } from "@/lib/api";
import { RefreshCw, Filter, Droplet, Sparkles, UtensilsCrossed, Footprints, GlassWater, Stethoscope, MoveUp, Clock, Wrench, AlertCircle } from "lucide-react";

const TYPE_META = {
  "washroom-male":   { label: "Washroom · M", icon: Footprints,        color: "hsl(217 91% 60%)" },
  "washroom-female": { label: "Washroom · F", icon: Footprints,        color: "hsl(330 81% 60%)" },
  "food-stall":      { label: "Food Stall",   icon: UtensilsCrossed,   color: "hsl(var(--warning))" },
  "water-point":     { label: "Water Point",  icon: GlassWater,        color: "hsl(var(--primary))" },
  "first-aid":       { label: "First Aid",    icon: Stethoscope,       color: "hsl(var(--destructive))" },
  "escalator":       { label: "Escalator",    icon: MoveUp,            color: "hsl(var(--muted-foreground))" },
};

const FILTERS = [
  { id: "all",               label: "All" },
  { id: "washroom-male",     label: "Washroom · M" },
  { id: "washroom-female",   label: "Washroom · F" },
  { id: "food-stall",        label: "Food" },
  { id: "water-point",       label: "Water" },
  { id: "first-aid",         label: "First Aid" },
  { id: "escalator",         label: "Escalator" },
];

const fmtRel = (iso) => {
  if (!iso) return "—";
  const d = (Date.now() - new Date(iso).getTime()) / 1000;
  if (d < 60) return `${Math.max(1, Math.floor(d))}s ago`;
  if (d < 3600) return `${Math.floor(d / 60)}m ago`;
  return `${Math.floor(d / 3600)}h ago`;
};

const Tile = ({ icon: Icon, label, value, sub, accent, testid }) => (
  <div className="panel p-3 flex flex-col gap-0.5 min-w-0" data-testid={testid}>
    <div className="flex items-center justify-between">
      <span className="micro-label">{label}</span>
      <Icon className="w-3.5 h-3.5 text-muted-foreground" strokeWidth={1.5} />
    </div>
    <div className={`font-mono text-2xl font-semibold tracking-tighter leading-none mt-1 ${accent || ""}`}>{value}</div>
    <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mt-0.5 truncate">{sub}</div>
  </div>
);

const queueColor = (pct) =>
  pct >= 1 ? "hsl(var(--destructive))" :
  pct >= 0.8 ? "hsl(var(--warning))" :
  pct >= 0.5 ? "hsl(var(--primary))" :
                "hsl(var(--success))";

export default function Facilities() {
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("all");
  const [refreshing, setRefreshing] = useState(false);

  const load = async () => {
    setRefreshing(true);
    try {
      const r = await http.get("/facilities/queues").then((x) => x.data);
      setData(r);
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 7000);
    return () => clearInterval(t);
  }, []);

  const filtered = useMemo(() => {
    if (!data?.facilities) return [];
    if (filter === "all") return data.facilities;
    return data.facilities.filter((f) => f.type === filter);
  }, [data, filter]);

  const summary = data?.summary;

  return (
    <div className="h-full flex flex-col overflow-hidden">
      <div className="h-10 flex items-center justify-between px-4 border-b border-border bg-[hsl(var(--surface))] shrink-0">
        <div className="flex items-baseline gap-3">
          <h1 className="font-display font-black uppercase tracking-tight text-sm leading-none">Facilities</h1>
          <span className="micro-label hidden sm:inline">Queues · Sanitation · Safety</span>
        </div>
        <button onClick={load} className="btn-tac !px-2 !py-1.5" data-testid="fac-refresh-btn" aria-label="Refresh">
          <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? "animate-spin" : ""}`} />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-2 space-y-2 min-h-0">
        {/* KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          <Tile icon={Clock}      label="Avg Wait Time"      value={`${summary?.avg_wait_min ?? 0}m`} sub={`${summary?.total ?? 0} facilities`} testid="fac-kpi-wait" />
          <Tile icon={AlertCircle} label="SLA Breaches"      value={summary?.sla_breaches ?? 0}        sub="queues over target wait" accent={(summary?.sla_breaches ?? 0) > 0 ? "sev-medium" : ""} testid="fac-kpi-breaches" />
          <Tile icon={Droplet}    label="Wet-Floor Alerts"   value={summary?.wet_floor_alerts ?? 0}     sub="spills awaiting cleanup" accent={(summary?.wet_floor_alerts ?? 0) > 0 ? "sev-medium" : ""} testid="fac-kpi-wet" />
          <Tile icon={Sparkles}   label="Slowest"            value={`${summary?.slowest?.wait_min ?? 0}m`} sub={summary?.slowest?.name ?? "—"} testid="fac-kpi-slowest" />
        </div>

        {/* Filter row */}
        <div className="panel p-2 flex items-center gap-1.5 flex-wrap">
          <Filter className="w-3.5 h-3.5 text-muted-foreground" />
          <span className="micro-label mr-1">Filter</span>
          {FILTERS.map((f) => (
            <button key={f.id} onClick={() => setFilter(f.id)} data-testid={`fac-filter-${f.id}`}
              className={["px-2 py-0.5 text-[10px] uppercase tracking-widest font-bold border transition-colors",
                filter === f.id ? "border-[hsl(var(--primary))] text-[hsl(var(--primary))]" : "border-border text-muted-foreground hover:text-foreground"].join(" ")}>
              {f.label}
            </button>
          ))}
          <span className="ml-auto font-mono text-[10px] text-muted-foreground">{filtered.length} / {data?.facilities?.length ?? 0}</span>
        </div>

        {/* Facility cards grid */}
        <div className="grid gap-2" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))" }} data-testid="fac-grid">
          {filtered.map((f) => {
            const meta = TYPE_META[f.type] || { label: f.type, icon: Wrench, color: "hsl(var(--primary))" };
            const Icon = meta.icon;
            const fillPct = Math.min(1.2, f.queue / f.capacity);
            const barPct = Math.min(100, fillPct * 100);
            const barColor = queueColor(fillPct);
            return (
              <div key={f.id} className="panel p-3 flex flex-col gap-2" data-testid={`fac-card-${f.id}`}>
                <div className="flex items-start gap-2">
                  <Icon className="w-4 h-4 shrink-0 mt-0.5" style={{ color: meta.color }} />
                  <div className="min-w-0 flex-1">
                    <div className="font-display font-bold uppercase tracking-tight text-xs leading-tight truncate">{f.name}</div>
                    <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mt-0.5">{meta.label} · {f.zone_id}</div>
                  </div>
                  {f.wet_floor && (
                    <span className="px-1 py-0.5 text-[9px] uppercase tracking-widest font-bold border border-[hsl(var(--warning))] text-[hsl(var(--warning))] flex items-center gap-1 whitespace-nowrap">
                      <Droplet className="w-2.5 h-2.5" /> wet
                    </span>
                  )}
                </div>

                <div className="grid grid-cols-3 gap-2">
                  <div>
                    <div className="micro-label">Queue</div>
                    <div className="font-mono text-lg font-semibold tracking-tighter leading-none mt-0.5">{f.queue}<span className="text-muted-foreground text-xs">/{f.capacity}</span></div>
                  </div>
                  <div>
                    <div className="micro-label">Wait</div>
                    <div className={`font-mono text-lg font-semibold tracking-tighter leading-none mt-0.5 ${f.sla_breach ? "sev-medium" : ""}`}>{f.wait_min}<span className="text-muted-foreground text-xs">m</span></div>
                    <div className="font-mono text-[10px] text-muted-foreground">SLA {f.sla_minutes}m</div>
                  </div>
                  <div>
                    <div className="micro-label">Cleaned</div>
                    <div className={`font-mono text-lg font-semibold tracking-tighter leading-none mt-0.5 ${f.since_clean_min > 45 ? "sev-medium" : ""}`}>{f.since_clean_min}<span className="text-muted-foreground text-xs">m</span></div>
                    <div className="font-mono text-[10px] text-muted-foreground">{fmtRel(f.last_cleaned)}</div>
                  </div>
                </div>

                <div>
                  <div className="h-1.5 bg-border w-full">
                    <div className="h-full transition-all" style={{ width: `${barPct}%`, background: barColor }} />
                  </div>
                </div>
              </div>
            );
          })}
          {filtered.length === 0 && (
            <div className="col-span-full p-6 text-center text-xs text-muted-foreground">No facilities match this filter.</div>
          )}
        </div>
      </div>
    </div>
  );
}
